import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { ArrowLeft, Pencil } from 'lucide-react'
import { useMemo } from 'react'
import { AttachmentsPanel } from '@/components/crm/attachments-panel'
import { CommentsThread } from '@/components/crm/comments-thread'
import { EntityList, type EntityListColumn } from '@/components/crm/entity-list'
import { LayoutAwareDetail } from '@/components/crm/layout-aware-detail'
import { TagPicker } from '@/components/crm/tag-picker'
import { Timeline } from '@/components/crm/timeline'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/crm/accounts/$accountId')({
  component: AccountDetailPage,
})

type AccountRecord = {
  id: string
  name?: string | null
  website?: string | null
  industryId?: string | null
  ownerUserId?: string | null
  parentAccountId?: string | null
  _ext?: Record<string, unknown>
}

type ContactRow = {
  id: string
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  jobTitle?: string | null
  accountId?: string | null
}

type OpportunityRow = {
  id: string
  name?: string | null
  accountId?: string | null
  amount?: string | null
  currency?: string | null
  stageId?: string | null
  ownerUserId?: string | null
}

type ActivityRow = {
  id: string
  type?: string | null
  subject?: string | null
  dueAt?: string | Date | null
  completedAt?: string | Date | null
  relatedToType?: string | null
  relatedToId?: string | null
}

function formatDate(v: string | Date | null | undefined): string {
  if (!v) return '-'
  try {
    return new Date(v).toLocaleDateString()
  } catch {
    return '-'
  }
}

function fullName(r: ContactRow): string {
  return `${r.firstName ?? ''} ${r.lastName ?? ''}`.trim() || r.email || '(no name)'
}

function AccountDetailPage() {
  const t = useTranslations()
  const navigate = useNavigate()
  const { accountId } = Route.useParams()

  const { data, isLoading } = useQuery(
    orpc.crm.accounts.get.queryOptions({ input: { id: accountId } }),
  )

  const { data: contactRows } = useQuery(
    orpc.crm.contacts.list.queryOptions({ input: { limit: 500, offset: 0 } }),
  )
  const { data: opportunityRows } = useQuery(
    orpc.crm.opportunities.list.queryOptions({
      input: { limit: 500, offset: 0, accountId },
    }),
  )
  const { data: activityRows } = useQuery(
    orpc.crm.activities.list.queryOptions({
      input: { limit: 200, offset: 0, relatedToType: 'account', relatedToId: accountId },
    }),
  )

  const { data: parentAccount } = useQuery(
    orpc.crm.accounts.get.queryOptions({
      input: { id: (data as AccountRecord | undefined)?.parentAccountId ?? '' },
      enabled: !!(data as AccountRecord | undefined)?.parentAccountId,
    }),
  )

  const account = data as AccountRecord | undefined

  // Contacts are filtered client-side by accountId — the backend list
  // procedure doesn't yet accept an accountId filter (Phase 6 follow-up).
  const accountContacts = useMemo(() => {
    const all = (contactRows as ContactRow[] | undefined) ?? []
    return all.filter((c) => c.accountId === accountId)
  }, [contactRows, accountId])

  const accountOpportunities = (opportunityRows as OpportunityRow[] | undefined) ?? []
  const accountActivities = (activityRows as ActivityRow[] | undefined) ?? []

  if (isLoading || !account) {
    return (
      <div className="p-6">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  const title = account.name ?? '(unnamed account)'

  const contactColumns: EntityListColumn<ContactRow>[] = [
    {
      key: 'name',
      header: t('crm.account.name'),
      render: (r) => <span className="font-medium">{fullName(r)}</span>,
    },
    { key: 'jobTitle', header: t('crm.contact.jobTitle'), render: (r) => r.jobTitle ?? '-' },
    { key: 'email', header: t('crm.contact.email'), render: (r) => r.email ?? '-' },
  ]

  const opportunityColumns: EntityListColumn<OpportunityRow>[] = [
    {
      key: 'name',
      header: t('crm.opportunity.name'),
      render: (r) => <span className="font-medium">{r.name ?? '(unnamed)'}</span>,
    },
    {
      key: 'amount',
      header: t('crm.opportunity.amount'),
      render: (r) => (r.amount ? `${r.amount} ${r.currency ?? ''}`.trim() : '-'),
    },
    { key: 'owner', header: t('crm.common.ownedBy'), render: (r) => r.ownerUserId ?? '-' },
  ]

  const activityColumns: EntityListColumn<ActivityRow>[] = [
    {
      key: 'type',
      header: t('crm.activity.type'),
      render: (r) => <span className="capitalize">{r.type ?? '-'}</span>,
    },
    { key: 'subject', header: t('crm.activity.subject'), render: (r) => r.subject ?? '-' },
    { key: 'dueAt', header: t('crm.activity.dueAt'), render: (r) => formatDate(r.dueAt) },
    {
      key: 'completedAt',
      header: t('crm.activity.completedAt'),
      render: (r) =>
        r.completedAt ? (
          formatDate(r.completedAt)
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
    },
  ]

  const parent = parentAccount as AccountRecord | undefined

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/crm/accounts' })}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('crm.common.cancel')}
        </Button>
      </div>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">{title}</h1>
          <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
            {account.website && <span>{account.website}</span>}
            {account.ownerUserId && (
              <span>
                {t('crm.common.ownedBy')}: {account.ownerUserId}
              </span>
            )}
            {parent && (
              <span>
                {t('crm.account.parentAccount')}:{' '}
                <Button
                  variant="link"
                  className="h-auto p-0"
                  onClick={() =>
                    navigate({
                      to: '/crm/accounts/$accountId',
                      params: { accountId: parent.id },
                    })
                  }
                >
                  {parent.name ?? parent.id}
                </Button>
              </span>
            )}
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate({ to: '/crm/accounts/$accountId/edit', params: { accountId } })}
        >
          <Pencil className="h-4 w-4 mr-2" />
          {t('crm.common.edit')}
        </Button>
      </div>

      <Tabs defaultValue="details" className="w-full">
        <TabsList>
          <TabsTrigger value="details">{t('navigation.details')}</TabsTrigger>
          <TabsTrigger value="contacts">{t('crm.contact.entityPlural')}</TabsTrigger>
          <TabsTrigger value="opportunities">{t('crm.opportunity.entityPlural')}</TabsTrigger>
          <TabsTrigger value="activities">{t('crm.activity.entityPlural')}</TabsTrigger>
          <TabsTrigger value="timeline">{t('navigation.timeline')}</TabsTrigger>
          <TabsTrigger value="tags">{t('navigation.inputTags')}</TabsTrigger>
          <TabsTrigger value="comments">{t('navigation.chat')}</TabsTrigger>
          <TabsTrigger value="attachments">{t('navigation.fileDropzone')}</TabsTrigger>
        </TabsList>

        <TabsContent value="details">
          <LayoutAwareDetail
            entityKey="account"
            record={account as Record<string, unknown> & { id: string }}
          />
        </TabsContent>

        <TabsContent value="contacts">
          <Card>
            <CardHeader>
              <CardTitle>{t('crm.contact.entityPlural')}</CardTitle>
            </CardHeader>
            <CardContent>
              <EntityList
                rows={accountContacts}
                columns={contactColumns}
                onRowClick={(r) =>
                  navigate({ to: '/crm/contacts/$contactId', params: { contactId: r.id } })
                }
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="opportunities">
          <Card>
            <CardHeader>
              <CardTitle>{t('crm.opportunity.entityPlural')}</CardTitle>
            </CardHeader>
            <CardContent>
              <EntityList
                rows={accountOpportunities}
                columns={opportunityColumns}
                onRowClick={(r) =>
                  navigate({
                    to: '/crm/opportunities/$opportunityId',
                    params: { opportunityId: r.id },
                  })
                }
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activities">
          <Card>
            <CardHeader>
              <CardTitle>{t('crm.activity.entityPlural')}</CardTitle>
            </CardHeader>
            <CardContent>
              <EntityList rows={accountActivities} columns={activityColumns} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="timeline">
          <Card>
            <CardHeader>
              <CardTitle>{t('navigation.timeline')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Timeline recordType="account" recordId={accountId} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tags">
          <Card>
            <CardHeader>
              <CardTitle>{t('navigation.inputTags')}</CardTitle>
            </CardHeader>
            <CardContent>
              <TagPicker recordType="account" recordId={accountId} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="comments">
          <Card>
            <CardHeader>
              <CardTitle>{t('navigation.chat')}</CardTitle>
            </CardHeader>
            <CardContent>
              <CommentsThread recordType="account" recordId={accountId} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="attachments">
          <Card>
            <CardHeader>
              <CardTitle>{t('navigation.fileDropzone')}</CardTitle>
            </CardHeader>
            <CardContent>
              <AttachmentsPanel recordType="account" recordId={accountId} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
